import { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSite } from "@/api/hooks/sites";
import { useSiteEnrolledDiseaseAreas } from "@/api/hooks/siteDiseaseAreas";
import { setSiteContext } from "@/api/siteContext";
import { SiteHeader } from "./SiteHeader";
import { SiteInfoCards } from "./SiteInfoCards";
import { SiteAdministrationPanel } from "./SiteAdministrationPanel";
import { ADD_DA_TAB, DiseaseAreaTabBar } from "./DiseaseAreaTabBar";
import { DiseaseAreaPanel } from "./DiseaseAreaPanel";
import { AddDiseaseAreaDialog } from "./AddDiseaseAreaDialog";

export default function SiteDetail() {
  const { id, da_code } = useParams<{ id: string; da_code?: string }>();
  const navigate = useNavigate();
  const site = useSite(id);
  const das = useSiteEnrolledDiseaseAreas(id);

  const [addDaOpen, setAddDaOpen] = useState(false);

  const enrolledDaIds = useMemo(
    () => new Set((das.data ?? []).map((d) => d.id)),
    [das.data],
  );

  useEffect(() => {
    if (id && !da_code && das.data && das.data.length > 0) {
      const first = das.data[0].code;
      navigate(`/clinops/sites/${id}/da/${first}`, { replace: true });
    }
  }, [id, da_code, das.data, navigate]);

  // Register the site context so every API call from this page (CV
  // uploads, document views, etc.) carries the X-Site-Context header.
  // Audit emits then stamp payload.site_id so the audit log shows
  // "002 · BCM" instead of falling back to the most-recent SDA-I
  // assignment for the investigator.
  useEffect(() => {
    if (!id) return;
    setSiteContext(id);
    return () => setSiteContext(null);
  }, [id]);

  if (site.isLoading || das.isLoading || !site.data || !das.data) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Map Site fields into SiteHeaderView for the header/info cards.
  // Status is set to a default for this slice; the DA view refines it
  // when a specific ClinOpsView is loaded (via DiseaseAreaPanel).
  const headerView = {
    id: site.data.id,
    display_id: site.data.display_id ?? null,
    common_name: site.data.common_name,
    legal_entity_name: site.data.legal_entity_name ?? null,
    street: site.data.street ?? null,
    city: site.data.city ?? null,
    state: site.data.state ?? null,
    postal_code: site.data.postal_code ?? null,
    allowed_email_domains: site.data.allowed_email_domains ?? [],
    status: "start_up" as const, // refined once DA view loads
    primary_contact: null,
  };

  const active = da_code ?? (das.data.length > 0 ? das.data[0].code : "");
  const hasEnrolled = das.data.length > 0;

  return (
    <Box>
      <SiteHeader
        site={headerView}
        onBack={() => navigate("/clinops/sites")}
        onEdit={() => navigate(`/clinops/sites/${id}/edit`)}
      />
      <SiteInfoCards site={headerView} />
      {/* Contacts + Locations live in one collapsible panel. They're
          set up at onboarding and rarely touched after, so hiding them
          by default keeps the disease-area tabs the primary focus. */}
      <SiteAdministrationPanel siteId={id!} />
      <DiseaseAreaTabBar
        diseaseAreas={das.data.map((d) => ({ code: d.code, name: d.name }))}
        active={active}
        onSelect={(v) => {
          if (v === ADD_DA_TAB) {
            setAddDaOpen(true);
            return;
          }
          navigate(`/clinops/sites/${id}/da/${v}`);
        }}
      />
      {!hasEnrolled ? (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: "center", py: 5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No disease areas yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2, fontSize: 14 }}>
              Enroll a site location in a disease area to start tracking milestones, PIs,
              and pharma studies here.
            </Typography>
            <Button variant="contained" onClick={() => setAddDaOpen(true)}>
              Add disease area
            </Button>
          </CardContent>
        </Card>
      ) : da_code ? (
        <DiseaseAreaPanel siteId={id!} daCode={da_code} />
      ) : null}

      <AddDiseaseAreaDialog
        open={addDaOpen}
        onClose={() => setAddDaOpen(false)}
        siteId={id!}
        enrolledDaIds={enrolledDaIds}
        onAdded={(code) => navigate(`/clinops/sites/${id}/da/${code}`)}
      />
    </Box>
  );
}
